import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    HostListener,
    input,
    output,
    signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { form } from '@angular/forms/signals';
import { ShIconComponent } from '@stylehaven/components';

import { ExportFailureBanner } from '../entities/downloads/export-failure.entity';
import { ExportScopeKind } from '../entities/downloads/export-scope.entity';

/** The three mutually-exclusive content choices, mapped to the backend booleans on emit. */
export type ExportContentMode = 'files' | 'metadata' | 'filesAndMetadata';

/** Above this, an export is refused outright — the user must narrow the scope first. */
const MAX_EXPORT_COUNT = 1000;

/** Payload emitted on "Start download" — shaped to the backend export-trigger contract (HFBD-30/31/32). */
export interface ExportStartPayload {
    includeFiles: boolean;
    includeMetadata: boolean;
    /** The chosen scope; the container turns it into an `ExportScope` (HFBD-86). */
    scope: ExportScopeKind;
    /** Reserved: SIP is dropped for now (HFBD-53 ticket 001); re-add without reshaping the contract. */
    includeSip?: boolean;
}

/**
 * Presentational export sidepanel (HFBD-53). Rendered as an overlay above the filter panel by
 * `RecordOverviewContainerComponent`; all-inputs / one-output, never fetches or calls the backend.
 * Specs: notes/docs/technical-spec/hfbd-53-export-sidepanel.md and hfbd-86-export-selection-modes.md.
 */
@Component({
    standalone: true,
    selector: 'digihaven-export-sidepanel',
    templateUrl: './export-sidepanel.component.html',
    styleUrls: ['./export-sidepanel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, TranslateModule, ShIconComponent]
})
export class ExportSidepanelComponent {
    /** Object count of the current filtered selection; `null` while unknown (loading or error). */
    readonly count = input<number | null>(null);
    /** Number of rows the user ticked in the overview; gates the "aangepaste selectie" scope. */
    readonly selectedCount = input(0);
    /** Estimated total size in bytes; `null`/absent hides the size line (size may be unavailable, HFBD-33). */
    readonly totalSizeBytes = input<number | null>(null);
    /** A count/size recalculation is in flight. */
    readonly loading = input(false);
    /** The selection count could not be determined (the overview owns the primary error surface). */
    readonly hasError = input(false);
    /** An export launch is in flight (driven by the sibling wiring); disables "Start download". */
    readonly submitting = input(false);
    readonly failure = input<ExportFailureBanner | null>(null);

    /** Emitted when the user dismisses the panel (back-arrow or Esc). */
    readonly close = output<void>();
    /** Emitted on "Start download" with the chosen scope kind and the content mode as backend booleans. */
    readonly startDownload = output<ExportStartPayload>();
    readonly scopeChanged = output<ExportScopeKind>();

    /** Scope as an Angular Signal Forms field; kept in sync with `selectedCount` (see constructor). */
    private readonly scopeModel = signal<{ scope: ExportScopeKind }>({ scope: 'filtered' });
    readonly scopeForm = form(this.scopeModel);

    /**
     * Content mode as an Angular Signal Forms field; default "Bestanden en metadata", the one mode that never
     * fails on a stuk without a digital representatie (HFBD-171).
     */
    private readonly model = signal<{ mode: ExportContentMode }>({ mode: 'filesAndMetadata' });
    readonly contentForm = form(this.model);

    readonly scope = computed<ExportScopeKind>(() => this.scopeForm.scope().value());

    readonly scopeOptions = computed<
        ReadonlyArray<{ value: ExportScopeKind; labelKey: string; count: string; disabled: boolean }>
    >(() => [
        {
            value: 'selected',
            labelKey: 'exports.sidepanel.scope.selected',
            count: this.formatCount(this.selectedCount()),
            disabled: this.selectedCount() === 0
        },
        {
            value: 'filtered',
            labelKey: 'exports.sidepanel.scope.filtered',
            count: this.formattedCount(),
            disabled: false
        }
    ]);

    readonly modeOptions: ReadonlyArray<{ value: ExportContentMode; labelKey: string; format: string }> = [
        { value: 'filesAndMetadata', labelKey: 'exports.sidepanel.mode.files-and-metadata', format: '.zip' },
        { value: 'files', labelKey: 'exports.sidepanel.mode.files', format: '.zip' },
        { value: 'metadata', labelKey: 'exports.sidepanel.mode.metadata', format: '.zip' }
    ];

    readonly mode = computed<ExportContentMode>(() => this.contentForm.mode().value());
    /** Count of the scope the user actually picked — what "Start download" is gated on. */
    readonly activeCount = computed(() => (this.scope() === 'selected' ? this.selectedCount() : (this.count() ?? 0)));
    readonly exceedsMax = computed(() => this.activeCount() > MAX_EXPORT_COUNT);
    readonly showsFailure = computed(() => !!this.failure() && !this.exceedsMax());
    /** Format/content choices only make sense once there is something exportable to apply them to. */
    readonly hasValidSelection = computed(() => this.activeCount() > 0 && !this.exceedsMax());
    readonly canStart = computed(() => this.hasValidSelection() && !this.submitting());
    readonly maxExportCount = MAX_EXPORT_COUNT;

    readonly showSkeleton = computed(() => this.loading() || (this.count() === null && !this.hasError()));
    /** Both scopes, not just the filtered one: a selection outlives the filters that no longer match it. */
    readonly isEmpty = computed(() => this.count() === 0 && this.selectedCount() === 0);
    readonly formattedCount = computed(() => this.formatCount(this.count()));
    readonly formattedSize = computed(() => {
        const bytes = this.totalSizeBytes();
        return bytes == null ? null : this.humanReadableBytes(bytes);
    });

    constructor() {
        /** Re-sync to "aangepaste selectie" every time the ticked-row count changes, overriding any manual pick. */
        effect(() => {
            this.scopeModel.set({ scope: this.selectedCount() > 0 ? 'selected' : 'filtered' });
        });
    }

    setScope(scope: ExportScopeKind): void {
        this.scopeForm.scope().value.set(scope);
        this.scopeChanged.emit(scope);
    }

    setMode(mode: ExportContentMode): void {
        this.contentForm.mode().value.set(mode);
    }

    onStart(): void {
        if (!this.canStart()) {
            return;
        }
        const mode = this.mode();
        this.startDownload.emit({
            includeFiles: mode === 'files' || mode === 'filesAndMetadata',
            includeMetadata: mode === 'metadata' || mode === 'filesAndMetadata',
            scope: this.scope()
        });
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        this.close.emit();
    }

    private formatCount(value: number | null): string {
        return value == null ? '—' : new Intl.NumberFormat('nl-BE').format(value);
    }

    private humanReadableBytes(bytes: number): string {
        if (bytes < 1024) {
            return `${bytes} B`;
        }
        const units = ['kB', 'MB', 'GB', 'TB', 'PB'];
        let size = bytes / 1024;
        let unitIndex = 0;
        while (size >= 1024 && unitIndex < units.length - 1) {
            size /= 1024;
            unitIndex++;
        }
        return `${new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 1 }).format(size)} ${units[unitIndex]}`;
    }
}
